import { Box, CircularProgress } from '@mui/material';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetMonthlyDataQuery,
  useGetOccupancyQuery,
  useGetPortfolioConsumptionDataQuery,
  useGetPortfolioDurationAnalysisQuery,
  useGetPortfolioHourAnalysisQuery,
  usePostPortfolioUpdateNowMutation,
  useSetDaytimeIntervalMutation,
} from '../../../api/energy-analysis/portfolioAnalysisApiSlice';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';

import { DateTime } from 'luxon';
import { showSnackbar } from '../../../redux/snackbarSlice';
import DurationAnalysisDialog from '@/features/portfolio-analysis/components/DurationAnalysisDialog';
import HourAnalysisDialog from '@/features/portfolio-analysis/components/HourAnalysisDialog';
import PortfolioAnalysisDataChart from '@/features/portfolio-analysis/components/PortfolioAnalysisDataChart';
import PortfolioAnalysisGroupedColumnChart from '@/features/portfolio-analysis/components/PortfolioAnalysisGroupedColumnChart';
import { downLoadCsvFile } from '@/utils/export';
import EditMatrikelDataDialog from '@/features/portfolio-analysis/components/EditMatrikelDataDialog';
import EditRowDataDialog from '@/features/portfolio-analysis/components/EditRowDataDialog';
import MeterDrawer from '@/features/portfolio-analysis/components/MeterDrawer';

const MeterContainer = ({
  isOpen,
  onClose,
  meter,
  meterType,
  analysisType,
}) => {
  const data_stream = JSON.parse(meter.data_stream);

  const meterId = useMemo(() => {
    return analysisType === 'power'
      ? meter.metering_point_id
      : data_stream.unit_id;
  }, [analysisType, data_stream?.unit_id, meter.metering_point_id]);

  const dispatch = useDispatch();

  const account = useSelector(getSelectedAccount);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: DateTime.now().minus({ weeks: 4 }).startOf('day').toMillis(),
    end: DateTime.now().startOf('day').toMillis(),
  });
  const { data: occupancyData } = useGetOccupancyQuery(
    {
      recalc_account_id: account?.AccountId,
      meter_id: meterId,
      analysis_type: analysisType,
    },
    { skip: !account?.AccountId || !meterId || meterType === 'matrikel' },
  );
  // Edit matrikel data feature
  const [selectedMatrikel, setSelectedMatrikel] = useState({});
  const [updatePortfolioAnalysis] = usePostPortfolioUpdateNowMutation();
  // Edit occupancy feature
  const [selectedRowSettings, setSelectedRowSettings] = useState({});

  const [setDaytimeInterval] = useSetDaytimeIntervalMutation();

  const handleSaveOccupancyChange = async ({ occupancy }) => {
    let response;
    response = await setDaytimeInterval({
      account_id: account?.AccountId,
      metering_point_id:
        analysisType === 'power'
          ? selectedRowSettings?.metering_point_id
          : data_stream?.unit_id,
      occupancy: occupancy,
      analysis_type: analysisType,
    });

    if (response?.error?.status) {
      console.error('Error updating row data:');
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdateringen',
          severity: 'error',
        }),
      );
      return;
    } else {
      dispatch(
        showSnackbar({
          message:
            'Ændringerne er gemt. Bemærk at der kan gå op til 2 timer før ændringen tager effekt.',
          severity: 'success',
        }),
      );
      updatePortfolioAnalysis({ recalc_account_id: account?.AccountId });
      setSelectedRowSettings({});
    }
  };

  // Charts
  const [selectedChart, setSelectedChart] = useState('consumption');

  // DurationAnalysisDialog feature

  const [durationAnalysisFilterOptions, setDurationAnalysisFilterOptions] =
    useState({});
  const {
    data: durationAnalysisData,
    isError: isDurationAnalysisError,
    isFetching: isDurationAnalysisFetching,
  } = useGetPortfolioDurationAnalysisQuery(
    {
      analysisType: analysisType,
      accountId: account?.AccountId,
      meterId: meterId,
      start: selectedDateRange?.start,
      end: selectedDateRange?.end,
      ...durationAnalysisFilterOptions,
    },
    {
      skip: !meterId || meterType === 'matrikel' || analysisType !== 'power',
    },
  );

  // Hour analysis feature

  const [hourAnalysisFilterOptions, setHourAnalysisFilterOptions] = useState(
    {},
  );
  const {
    data: hourAnalysisData,
    isError: isHourAnalysisError,
    isFetching: isHourAnalysisFetching,
  } = useGetPortfolioHourAnalysisQuery(
    {
      analysisType: analysisType,
      accountId: account?.AccountId,
      meterId: meterId,
      start: selectedDateRange?.start,
      end: selectedDateRange?.end,
      ...hourAnalysisFilterOptions,
    },
    {
      skip: !meterId || meterType === 'matrikel',
    },
  );

  //Groued column chart feature
  const {
    data: chartData,
    isError: isChartError,
    isFetching: isChartFetching,
  } = useGetMonthlyDataQuery(
    {
      analysisType: analysisType,
      recalc_account_id: account?.AccountId,
      meter_id: meterId,
    },
    {
      skip: !meterId || meterType === 'matrikel',
    },
  );

  //Consumption chart

  const {
    data: unitData,
    isError: isUnitError,
    isFetching: isUnitFetching,
  } = useGetPortfolioConsumptionDataQuery(
    {
      analysisType: analysisType,
      accountId: account?.AccountId,
      meterId: meterId,
      start: selectedDateRange?.start,
      end: selectedDateRange?.end,
    },
    {
      skip: !meterId || meterType === 'matrikel',
    },
  );

  const chartProps = useMemo(() => {
    switch (selectedChart) {
      case 'durationAnalysis':
        return {
          open: !!meterId,
          onClose: () => {},
          row: meter,
          data: durationAnalysisData,
          isLoading: isDurationAnalysisFetching,
          setFilterOptions: setDurationAnalysisFilterOptions,
        };
      case 'hourAnalysis':
        return {
          open: !!meterId,
          onClose: () => {},
          row: meter,
          data: hourAnalysisData,
          isLoading: isHourAnalysisFetching,
          setFilterOptions: setHourAnalysisFilterOptions,
          unit: analysisType === 'water' ? 'm³' : 'kWh',
        };
      case 'consumption':
        return {
          address: meter?.address,
          data: unitData,
          isLoading: isUnitFetching,
          unit: analysisType === 'water' ? 'm³' : 'kWh',
        };
      case 'yearOnYear':
        return {
          address: meter?.address,
          data: chartData,
          isLoading: isChartFetching,
          unit: analysisType === 'water' ? 'm³' : 'kWh',
        };
      default:
        return {};
    }
  }, [
    selectedChart,
    meterId,
    meter,
    durationAnalysisData,
    isDurationAnalysisFetching,
    hourAnalysisData,
    isHourAnalysisFetching,
    unitData,
    isUnitFetching,
    analysisType,
    chartData,
    isChartFetching,
  ]);

  const Chart = useMemo(() => {
    switch (selectedChart) {
      case 'durationAnalysis':
        return DurationAnalysisDialog;
      case 'hourAnalysis':
        return HourAnalysisDialog;
      case 'consumption':
        return PortfolioAnalysisDataChart;
      case 'yearOnYear':
        return PortfolioAnalysisGroupedColumnChart;
      default:
        return null;
    }
  }, [selectedChart]);

  useEffect(() => {
    if (
      isUnitError ||
      isChartError ||
      isHourAnalysisError ||
      isDurationAnalysisError
    )
      dispatch(
        showSnackbar({
          message: 'Der skete en uventet fejl ved hentning af data',
          severity: 'error',
        }),
      );
  }, [
    isUnitError,
    isChartError,
    isHourAnalysisError,
    isDurationAnalysisError,
    dispatch,
  ]);

  return (
    <>
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <MeterDrawer
          meter={{ ...meter, ...occupancyData }}
          meterId={meterId}
          meterType={meterType}
          open={isOpen}
          onClose={() => {
            onClose();
            setSelectedChart('consumption');
          }}
          setSelectedMatrikel={setSelectedMatrikel}
          setSelectedRowSettings={setSelectedRowSettings}
          Chart={Chart}
          chartProps={chartProps}
          setSelectedChart={setSelectedChart}
          selectedChart={selectedChart}
          setSelectedDateRange={setSelectedDateRange}
          selectedDateRange={selectedDateRange}
          downloadCsv={() =>
            downLoadCsvFile(unitData.Data, `${meter.address}_data.csv`)
          }
          analysisType={analysisType}
        >
          <Suspense fallback={<CircularProgress />}>
            <EditMatrikelDataDialog
              open={!!selectedMatrikel?.matrikel_key}
              onClose={() => setSelectedMatrikel({})}
              address={selectedMatrikel?.address}
              matrikelKey={selectedMatrikel?.matrikel_key}
            />
          </Suspense>
          <Suspense fallback={<CircularProgress />}>
            <EditRowDataDialog
              open={!!selectedRowSettings?.data_stream}
              onClose={() => setSelectedRowSettings({})}
              row={occupancyData}
              saveChanges={handleSaveOccupancyChange}
              address={selectedRowSettings?.address}
            />
          </Suspense>
        </MeterDrawer>
      </Suspense>
    </>
  );
};

export default MeterContainer;
